/**
 * ============================================
 * 🔹 Click Sorvetes — Utilitários de Agenda e Lembretes
 * ============================================
 * Depende de utils/core/dateUtils.js
 * Gera links e arquivos .ics compatíveis com Google, Apple e Outlook.
 */

import {
  parseDDMM,
  formatYYYYMMDD,
  formatarDataISO,
  addDays,
  getCurrentYear,
} from "@/utils";

// Configuração global de antecedência padrão (dias antes do evento)
export const DEFAULT_ANTECEDENCIA_DIAS = 7;

/**
 * Gera link do evento oficial no Google Calendar.
 * @param {Object} evento - Objeto do calendário (deve conter dataInicio e dataFim)
 * @returns {string} URL do Google Calendar
 */
export function gerarLinkEvento(evento) {
  if (!evento?.dataInicio) return "#";

  const ano = getCurrentYear();
  const titulo = encodeURIComponent(evento.evento || "Campanha Click Sorvetes");
  const descricao = encodeURIComponent(
    `${evento.descricao || ""} — Campanha sugerida pela Click Sorvetes 🍦`
  );

  const inicioISO = formatarDataISO(evento.dataInicio, ano, "T090000Z");
  const fimISO = formatarDataISO(evento.dataFim || evento.dataInicio, ano, "T170000Z");

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${titulo}&dates=${inicioISO}/${fimISO}&details=${descricao}`;
}

/**
 * Gera link de lembrete (evento antecipado) no Google Calendar.
 * @param {Object} evento
 * @param {Object} [opts]
 * @param {number} [opts.antecedenciaDias=7] - Dias antes do evento
 * @returns {string}
 */
export function gerarLinkLembrete(evento, opts = {}) {
  if (!evento?.dataInicio) return "#";

  const antecedenciaDias = opts.antecedenciaDias || DEFAULT_ANTECEDENCIA_DIAS;
  const ano = getCurrentYear();
  const dataInicio = parseDDMM(evento.dataInicio, ano);
  const lembrete = addDays(dataInicio, -antecedenciaDias);

  const titulo = encodeURIComponent(`Lembrete: ${evento.evento}`);
  const descricao = encodeURIComponent(
    `Preparar campanha: ${evento.evento}. ${evento.descricao || ""}`
  );

  const inicioISO = `${formatYYYYMMDD(lembrete)}T090000Z`;
  const fimISO = `${formatYYYYMMDD(lembrete)}T170000Z`;

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${titulo}&dates=${inicioISO}/${fimISO}&details=${descricao}`;
}

/**
 * Gera um arquivo .ICS com alarmes automáticos (D-7, D-10, etc.)
 * @param {Object} evento
 * @param {Object} [opts]
 * @param {number[]} [opts.alarmsDias=[7]] - Quantos dias antes o alarme deve disparar
 * @returns {{ filename: string, content: string }}
 */
export function gerarICSComAlarme(evento, opts = {}) {
  if (!evento?.dataInicio) return null;

  const ano = getCurrentYear();
  const alarmsDias = opts.alarmsDias?.length ? opts.alarmsDias : [DEFAULT_ANTECEDENCIA_DIAS];

  const inicio = parseDDMM(evento.dataInicio, ano);
  const fim = parseDDMM(evento.dataFim || evento.dataInicio, ano);
  const dtStart = formatYYYYMMDD(inicio);
  const dtEnd = formatYYYYMMDD(addDays(fim, 1));

  const uid = `click-${evento.id || Math.random().toString(36).slice(2)}@click-sorvetes`;
  const summary = evento.evento || "Campanha Click Sorvetes";
  const descricao = (evento.descricao || "").replace(/\r?\n/g, "\\n");

  const alarms = alarmsDias
    .map(
      (dias) => [
        "BEGIN:VALARM",
        "ACTION:DISPLAY",
        `DESCRIPTION:Lembrete de campanha: ${summary}`,
        `TRIGGER:-P${dias}D`,
        "END:VALARM",
      ].join("\r\n")
    )
    .join("\r\n");

  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Click Sorvetes//Calendário de Marketing//PT-BR",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatYYYYMMDD(new Date())}T000000Z`,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${dtEnd}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${descricao}`,
    alarms,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const filename = `lembrete-${summary.toLowerCase().replace(/[^\w]+/g, "-")}.ics`;

  return { filename, content };
}

/**
 * Gera um .ICS recorrente diário, iniciando X dias antes e repetindo até a data do evento.
 * @param {Object} evento
 * @param {Object} [opts]
 * @param {number} [opts.diasAntes=DEFAULT_ANTECEDENCIA_DIAS]
 * @returns {{ filename: string, content: string }}
 */
export function gerarICSLembreteDiario(evento, opts = {}) {
  if (!evento?.dataInicio) return null;

  const { diasAntes = DEFAULT_ANTECEDENCIA_DIAS } = opts;
  const ano = getCurrentYear();

  const inicioEvt = parseDDMM(evento.dataInicio, ano);
  const fimEvt = parseDDMM(evento.dataFim || evento.dataInicio, ano);
  const inicioRec = addDays(inicioEvt, -diasAntes);

  const dtStart = formatYYYYMMDD(inicioRec);
  const dtEnd = formatYYYYMMDD(addDays(inicioRec, 1));
  const until = formatYYYYMMDD(fimEvt); // repete diariamente até a data do evento

  const uid = `click-rec-${evento.id || Math.random().toString(36).slice(2)}@click-sorvetes`;
  const summary = `Lembrete: ${evento.evento || "Campanha Click Sorvetes"}`;
  const descricao = (evento.descricao || "Preparar campanha").replace(/\r?\n/g, "\\n");

  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Click Sorvetes//Calendário de Marketing//PT-BR",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatYYYYMMDD(new Date())}T000000Z`,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${dtEnd}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${descricao}`,
    `RRULE:FREQ=DAILY;UNTIL=${until}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const filename = `lembrete-diario-${(evento.evento || "campanha")
    .toLowerCase()
    .replace(/[^\w]+/g, "-")}.ics`;

  return { filename, content };
}

/**
 * Adiciona lembrete conforme a escolha do usuário.
 * Se for Google → abre o link no Google Calendar;
 * Caso contrário → baixa o arquivo .ICS (com ou sem recorrência).
 *
 * @param {Object} evento - Evento (deve conter dataInicio)
 * @param {number} diasAntes - Quantos dias antes avisar
 * @param {boolean} recorrente - Se deve criar lembrete diário até o evento
 * @param {"google"|"outro"} tipoCalendario - Tipo de calendário escolhido pelo usuário
 */
export function adicionarLembreteInteligente(
  evento,
  diasAntes = 7,
  recorrente = false,
  tipoCalendario = "google"
) {
  if (!evento?.dataInicio) return;

  // GOOGLE — sem recorrência
  if (tipoCalendario === "google") {
    const link = gerarLinkLembrete(evento, { antecedenciaDias: diasAntes });
    try {
      const opened = window.open(link, "_blank");
      if (!opened) {
        setTimeout(() => {
          window.$toast?.warning?.(
            "🔒 Pop-up bloqueado. Clique novamente para abrir o Google Calendar."
          ) ?? alert("Pop-up bloqueado. Clique novamente para abrir o Google Calendar.");
        }, 300);
      }
    } catch (e) {
      console.warn(e);
      alert("Não foi possível abrir o Google Calendar.");
    }
    return;
  }

  // OUTROS (arquivo .ICS)
  const file = recorrente
    ? gerarICSLembreteDiario(evento, { diasAntes })
    : gerarICSComAlarme(evento, { alarmsDias: [diasAntes] });

  if (!file?.content) {
    alert("Não foi possível gerar o lembrete. Tente novamente.");
    return;
  }

  try {
    const blob = new Blob([file.content], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.filename;
    a.click();
    URL.revokeObjectURL(url);

    window.$toast?.success?.("✅ Lembrete (.ics) criado com sucesso!");
  } catch (err) {
    console.error("Erro ao gerar arquivo .ICS:", err);
    alert("Não foi possível salvar o lembrete automaticamente.");
  }
}
